// editor-api.js - EditorAPI: Tabs, Auto-Save, Grammatik-Check, Copy-to-Word

import { showConfirmation } from "../modal.js";
import { diffView } from "../diff-view.js";
import { wordHtml } from "./word-html.js";

// EditorAPI for external access to editor
export class EditorAPI {
  constructor(view) {
    this.view = view;

    // Tab management
    this.tabs = new Map(); // tabId -> { name, content }
    this.activeTabId = 'default';
    this.tabCounter = 0;

    // Auto-save configuration
    this.autoSaveInterval = null;
    this.autoSaveIntervalMs = 30000; // 30 seconds
    this.storageExpirationMs = 3 * 60 * 60 * 1000; // 3 hours

    // Initialize default tab
    this.tabs.set('default', {
      name: 'Befund 1',
      content: ''
    });

    // Load saved data from localStorage
    this.loadFromStorage();

    // Initialize tab UI handlers
    this.initTabHandlers();

    // Start auto-save
    this.startAutoSave();

    // Clean up on page unload (tab/browser close)
    window.addEventListener('beforeunload', () => {
      this.saveCurrentTabContent();
      this.saveToStorage();
    });
  }
  
  getCursor() {
    const head = this.view.state.selection.main.head;
    const line = this.view.state.doc.lineAt(head);
    return { line: line.number, ch: head - line.from };
  }
  
  replaceRange(text, from, to) {
    let start = typeof from === 'object'
      ? this.view.state.doc.line(from.line).from + from.ch
      : from;
    let end = to !== undefined
      ? (typeof to === 'object'
          ? this.view.state.doc.line(to.line).from + to.ch
          : to)
      : start;
    this.view.dispatch({ changes: { from: start, to: end, insert: text } });
  }
  
  replaceSelection(text) {
    const sel = this.view.state.selection.main;
    this.view.dispatch({ changes: { from: sel.from, to: sel.to, insert: text } });
  }
  
  // New helper methods for toolbar interaction
  insertAtCursor(text) {
    const head = this.view.state.selection.main.head;
    this.view.dispatch({ changes: { from: head, insert: text } });
  }

  // Get the full text content of the editor
  getText() {
    return this.view.state.doc.toString();
  }

  // Replace all text in the editor
  setText(text) {
    // Skip if content is identical
    const currentText = this.view.state.doc.toString();
    if (currentText === text) return;

    const length = this.view.state.doc.length;
    this.view.dispatch({
      changes: { from: 0, to: length, insert: text }
    });
  }

  // Perform grammar check using the autocorrect MCP tool
  async checkGrammar() {
    const text = this.getText();

    if (!text.trim()) {
      console.warn('No text to check');
      return;
    }

    try {
      // Call the autocorrect MCP tool
      const response = await fetch('/api/mcp/tools/call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          process_id: 'autocorrect',
          params: {
            text: text,
            language: 'de'
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.corrected_text) {
        // Replace the text with the corrected version
        this.setText(result.corrected_text);
      }

    } catch (error) {
      console.error('Grammar check failed:', error);
      alert('Fehler beim Grammatik-Check: ' + error.message);
    }
  }

  // Copy editor content to clipboard in Word-compatible format
  async copyToWord() {
    const markdown = this.getText();

    if (!markdown.trim()) {
      console.warn('No text to copy');
      alert('Kein Text zum Kopieren vorhanden');
      return;
    }

    try {
      const styledHtml = wordHtml(markdown);

      // Copy both HTML and plain text to clipboard
      const htmlBlob = new Blob([styledHtml], { type: 'text/html' });
      const textBlob = new Blob([markdown], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      });

      await navigator.clipboard.write([clipboardItem]);

    } catch (error) {
      alert('Fehler beim Kopieren: ' + error.message);
    }
  }

  // ===== TAB MANAGEMENT =====

  initTabHandlers() {
    // New tab button
    const newTabBtn = document.getElementById('btn-new-tab');
    if (newTabBtn) {
      newTabBtn.addEventListener('click', () => this.createNewTab());
    }

    // Event delegation for tab clicks, close buttons, and double-click rename
    const tabsContainer = document.getElementById('editor-tabs');
    if (tabsContainer) {
      let clickTimer = null;
      let clickedTabId = null;

      tabsContainer.addEventListener('click', (e) => {
        const tab = e.target.closest('.editor-tab');
        const closeBtn = e.target.closest('.tab-close');

        if (closeBtn && tab) {
          e.stopPropagation();
          const tabId = tab.dataset.tabId;
          this.closeTab(tabId);
          return;
        }

        if (tab) {
          const tabId = tab.dataset.tabId;

          // Check if this is a double-click (same tab clicked twice quickly)
          if (clickedTabId === tabId && clickTimer) {
            // Double-click detected - start rename
            clearTimeout(clickTimer);
            clickTimer = null;
            clickedTabId = null;
            e.preventDefault();
            e.stopPropagation();
            this.startRenameTab(tabId, tab);
          } else {
            // Single click - switch tab IMMEDIATELY
            this.switchToTab(tabId);

            // Track for potential double-click
            clickedTabId = tabId;
            if (clickTimer) clearTimeout(clickTimer);
            clickTimer = setTimeout(() => {
              clickedTabId = null;
              clickTimer = null;
            }, 300);
          }
        }
      });
    }
  }

  createNewTab(name = null, content = '') {
    this.tabCounter++;
    const tabId = `tab-${this.tabCounter}`;

    // Calculate the next Befund number based on existing Befund tabs
    let tabName;
    if (name) {
      tabName = name;
    } else {
      const befundNumbers = [];
      for (const [, tab] of this.tabs) {
        const match = tab.name.match(/^Befund\s+(\d+)$/);
        if (match) {
          befundNumbers.push(parseInt(match[1]));
        }
      }
      // Find the next number (highest + 1)
      const nextNumber = befundNumbers.length > 0 ? Math.max(...befundNumbers) + 1 : 1;
      tabName = `Befund ${nextNumber}`;
    }

    // Save current tab content before switching
    this.saveCurrentTabContent();

    this.tabs.set(tabId, {
      name: tabName,
      content
    });

    // Add tab to UI
    this.renderTabs(true);

    // Switch to new tab
    this.switchToTab(tabId);

    return tabId;
  }

  async closeTab(tabId) {
    // Don't close if it's the only tab
    if (this.tabs.size <= 1) {
      return;
    }

    // Get tab data
    const tab = this.tabs.get(tabId);
    const tabName = tab ? tab.name : 'diesen Befund';

    // Ask for confirmation using inline strip
    const confirmed = await this.showTabCloseConfirmation(tabName);

    if (!confirmed) {
      return;
    }

    // Remove tab from storage and memory
    this.tabs.delete(tabId);
    this.removeTabFromStorage(tabId);

    // If closing active tab, switch to another one
    if (tabId === this.activeTabId) {
      const firstTabId = this.tabs.keys().next().value;
      this.switchToTab(firstTabId);
    }

    // Renumber tabs automatically
    this.renumberTabs();
  }

  /**
   * Show tab close confirmation strip and return promise
   */
  showTabCloseConfirmation(tabName) {
    return new Promise((resolve) => {
      const strip = document.getElementById('tab-close-confirm');
      const text = document.getElementById('tab-close-confirm-text');
      const yesBtn = document.getElementById('tab-close-confirm-yes');
      const noBtn = document.getElementById('tab-close-confirm-no');

      if (!strip || !text || !yesBtn || !noBtn) {
        resolve(false);
        return;
      }

      text.textContent = `"${tabName}" schließen?`;
      strip.style.display = 'flex';

      const cleanup = () => {
        strip.style.display = 'none';
        yesBtn.removeEventListener('click', onYes);
        noBtn.removeEventListener('click', onNo);
      };

      const onYes = () => {
        cleanup();
        resolve(true);
      };

      const onNo = () => {
        cleanup();
        resolve(false);
      };

      yesBtn.addEventListener('click', onYes);
      noBtn.addEventListener('click', onNo);
    });
  }

  switchToTab(tabId) {
    if (!this.tabs.has(tabId)) {
      return;
    }

    // Save current tab content first
    this.saveCurrentTabContent();

    // Der Diff gehoert zum verlassenen Tab — sonst verglichen wir gleich
    // dessen Grundlage mit dem Text des neuen Tabs.
    diffView.clear();

    // Update active tab ID
    this.activeTabId = tabId;

    // Update UI FIRST for instant visual feedback
    this.renderTabs(false);

    // Load tab content (can be slightly delayed, user sees tab switch instantly)
    const tab = this.tabs.get(tabId);
    this.setText(tab.content);
  }

  saveCurrentTabContent() {
    const currentTab = this.tabs.get(this.activeTabId);
    if (currentTab) {
      currentTab.content = this.getText();
    }
  }

  renameTab(tabId, newName) {
    const tab = this.tabs.get(tabId);
    if (tab) {
      tab.name = newName;
      this.renderTabs(true);
    }
  }

  startRenameTab(tabId, tabElement) {
    // Prevent starting rename if already in rename mode
    if (tabElement.querySelector('.tab-name-input')) {
      return;
    }

    // Find the tab name span
    const nameSpan = tabElement.querySelector('.tab-name');
    if (!nameSpan) {
      return;
    }

    const currentName = nameSpan.textContent;

    // Replace span with input field
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'tab-name-input';
    input.value = currentName;
    input.style.cssText = 'background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.3); color: inherit; font: inherit; padding: 2px 6px; border-radius: 3px; outline: none; width: 100px;';

    // Replace span with input
    nameSpan.replaceWith(input);

    // Small delay to ensure DOM is ready
    setTimeout(() => {
      input.focus();
      input.select();
    }, 10);

    // Handle save on Enter
    const handleSave = () => {
      const newName = input.value.trim();
      if (newName && newName !== currentName) {
        this.renameTab(tabId, newName);
      } else {
        // Restore original name if empty or unchanged
        this.renderTabs(true);
      }
    };

    // Handle cancel on Escape
    const handleCancel = () => {
      this.renderTabs(true);
    };

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      }
    });

    // Save on blur (losing focus)
    input.addEventListener('blur', handleSave);
  }

  renumberTabs() {
    // Get all tabs in order
    const tabEntries = Array.from(this.tabs.entries());

    let befundCounter = 1;

    // Iterate through tabs and renumber those matching "Befund X" pattern
    for (const [tabId, tab] of tabEntries) {
      const match = tab.name.match(/^Befund\s+\d+$/);
      if (match) {
        tab.name = `Befund ${befundCounter}`;
        befundCounter++;
      }
    }

    // Re-render tabs to show new numbers
    this.renderTabs(true);
  }

  renderTabs(fullRender = false) {
    const tabsContainer = document.getElementById('editor-tabs');
    if (!tabsContainer) return;

    // Fast path: only update active class if tabs haven't changed
    const existingTabs = tabsContainer.querySelectorAll('.editor-tab');
    if (!fullRender && existingTabs.length === this.tabs.size) {
      existingTabs.forEach(tabEl => {
        const isActive = tabEl.dataset.tabId === this.activeTabId;
        tabEl.classList.toggle('active', isActive);
      });
      return;
    }

    // Full render: recreate all tabs
    const newTabBtn = tabsContainer.querySelector('.new-tab-btn');
    tabsContainer.innerHTML = '';

    for (const [tabId, tab] of this.tabs) {
      const tabEl = document.createElement('div');
      tabEl.className = `editor-tab${tabId === this.activeTabId ? ' active' : ''}`;
      tabEl.dataset.tabId = tabId;

      const nameSpan = document.createElement('span');
      nameSpan.className = 'tab-name';
      nameSpan.textContent = tab.name;

      const closeBtn = document.createElement('button');
      closeBtn.className = 'tab-close';
      closeBtn.title = 'Schließen';
      closeBtn.textContent = '×';

      tabEl.appendChild(nameSpan);

      if (this.tabs.size > 1) {
        tabEl.appendChild(closeBtn);
      }

      tabsContainer.appendChild(tabEl);
    }

    if (newTabBtn) {
      tabsContainer.appendChild(newTabBtn);
    }
  }

  // Get tab info for external use
  getActiveTab() {
    return {
      id: this.activeTabId,
      ...this.tabs.get(this.activeTabId)
    };
  }

  getAllTabs() {
    return Array.from(this.tabs.entries()).map(([id, tab]) => ({
      id,
      ...tab,
      isActive: id === this.activeTabId
    }));
  }

  // ===== AUTO-SAVE FUNCTIONALITY =====

  startAutoSave() {
    // Auto-save every 30 seconds
    this.autoSaveInterval = setInterval(() => {
      this.saveCurrentTabContent();
      this.saveToStorage();
    }, this.autoSaveIntervalMs);
  }

  stopAutoSave() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }
  }

  saveToStorage() {
    try {
      // Save current tab content first
      this.saveCurrentTabContent();

      // Prepare data to save
      const dataToSave = {
        tabs: Array.from(this.tabs.entries()),
        activeTabId: this.activeTabId,
        tabCounter: this.tabCounter,
        timestamp: Date.now()
      };

      // Save to localStorage
      localStorage.setItem('editor-tabs-data', JSON.stringify(dataToSave));
    } catch (error) {
      // Silent fail
    }
  }

  loadFromStorage() {
    try {
      const savedData = localStorage.getItem('editor-tabs-data');

      if (!savedData) {
        return;
      }

      const data = JSON.parse(savedData);
      const now = Date.now();

      // Check if data is expired (older than 3 hours)
      if (data.timestamp && (now - data.timestamp > this.storageExpirationMs)) {
        localStorage.removeItem('editor-tabs-data');
        return;
      }

      // Restore tabs
      this.tabs.clear();
      for (const [id, tab] of data.tabs) {
        this.tabs.set(id, tab);
      }

      // Restore active tab and counter
      this.activeTabId = data.activeTabId || 'default';
      this.tabCounter = data.tabCounter || 0;

      // Load the active tab content into the editor
      const activeTab = this.tabs.get(this.activeTabId);
      if (activeTab) {
        this.setText(activeTab.content);
      }

      // Render tabs in UI
      this.renderTabs(true);
    } catch (error) {
      // Clear corrupted data
      localStorage.removeItem('editor-tabs-data');
    }
  }

  removeTabFromStorage(tabId) {
    try {
      const savedData = localStorage.getItem('editor-tabs-data');

      if (!savedData) return;

      const data = JSON.parse(savedData);

      // Remove tab from saved data
      data.tabs = data.tabs.filter(([id]) => id !== tabId);

      // If the removed tab was active, set new active tab
      if (data.activeTabId === tabId && data.tabs.length > 0) {
        data.activeTabId = data.tabs[0][0];
      }

      // Save updated data
      localStorage.setItem('editor-tabs-data', JSON.stringify(data));
    } catch (error) {
      // Silent fail
    }
  }

  clearStorage() {
    try {
      localStorage.removeItem('editor-tabs-data');
    } catch (error) {
      // Silent fail
    }
  }
}
